import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Crown, Target, Brain, Award, Briefcase, TrendingUp, Camera, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * PremiumUpgradeModal - Wiederverwendbare Komponente für Premium/Pro-Upgrade-Hinweise
 * 
 * @param {boolean} open - Modal offen/geschlossen
 * @param {function} onOpenChange - Callback für Modal-Status-Änderung
 * @param {string} type - 'mentor' | 'account_limit' | 'feature' | 'pro_screenshot'
 * @param {string} featureName - Name des Features (für type='feature')
 */
export const PremiumUpgradeModal = ({ 
  open, 
  onOpenChange, 
  type = 'mentor',
  featureName = ''
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const getContent = () => {
    switch (type) {
      case 'mentor':
        return {
          icon: <Crown className="h-8 w-8 text-purple-400" />,
          iconBg: 'from-purple-500/20 to-pink-500/20 border-purple-500/30',
          title: language === 'de' ? 'Premium Feature' : 'Premium Feature',
          description: language === 'de' 
            ? 'Der KI Trading Mentor ist exklusiv für Premium-Mitglieder verfügbar. Upgraden Sie jetzt, um personalisierte Trading-Analysen und Coaching zu erhalten.'
            : 'The AI Trading Mentor is exclusively available for Premium members. Upgrade now to get personalized trading analysis and coaching.',
          features: [
            {
              icon: <Target className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Detaillierte Performance-Analyse' : 'Detailed performance analysis'
            },
            {
              icon: <Brain className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'KI-gestütztes Trading-Coaching' : 'AI-powered trading coaching'
            },
            {
              icon: <Award className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Psychologische Insights' : 'Psychological insights'
            }
          ],
          buttonText: language === 'de' ? 'Jetzt upgraden' : 'Upgrade now'
        };
      
      case 'pro_screenshot':
        return {
          icon: <Camera className="h-8 w-8 text-emerald-400" />,
          iconBg: 'from-emerald-500/20 to-purple-500/20 border-emerald-500/30',
          title: language === 'de' ? 'Pro Feature' : 'Pro Feature',
          description: language === 'de' 
            ? 'Der KI-gestützte Screenshot-Import ist ab dem Pro-Abo verfügbar. Lade einfach einen Chart-Screenshot hoch und unsere KI erkennt automatisch alle Trade-Daten.'
            : 'AI-powered screenshot import is available from the Pro plan. Simply upload a chart screenshot and our AI will automatically detect all trade data.',
          features: [
            {
              icon: <Camera className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Automatische Erkennung von Symbol, Ein- & Ausstieg' : 'Automatic detection of symbol, entry & exit'
            },
            {
              icon: <Sparkles className="h-5 w-5 text-purple-400 flex-shrink-0" />,
              text: language === 'de' ? 'KI analysiert TradingView & andere Plattformen' : 'AI analyzes TradingView & other platforms'
            },
            {
              icon: <TrendingUp className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Spare Zeit beim Trade-Logging' : 'Save time when logging trades'
            }
          ],
          buttonText: language === 'de' ? 'Auf Pro upgraden' : 'Upgrade to Pro'
        };
      
      case 'account_limit':
        return {
          icon: <Crown className="h-8 w-8 text-purple-400" />,
          iconBg: 'from-purple-500/20 to-pink-500/20 border-purple-500/30',
          title: language === 'de' ? 'Konto-Limit erreicht' : 'Account Limit Reached',
          description: language === 'de' 
            ? 'Du hast das Maximum an Trading-Konten für deinen aktuellen Plan erreicht. Upgrade jetzt, um mehr Konten hinzuzufügen.'
            : 'You have reached the maximum number of trading accounts for your current plan. Upgrade now to add more accounts.',
          features: [
            {
              icon: <Briefcase className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Basic: 1 Konto | Pro: 5 Konten | Premium: Unbegrenzt' : 'Basic: 1 account | Pro: 5 accounts | Premium: Unlimited'
            },
            {
              icon: <TrendingUp className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Verwalte mehrere Broker und Strategien' : 'Manage multiple brokers and strategies'
            },
            {
              icon: <Target className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Separate Performance-Tracking' : 'Separate performance tracking'
            }
          ],
          buttonText: language === 'de' ? 'Jetzt upgraden' : 'Upgrade now'
        };
      
      case 'feature':
        return {
          icon: <Crown className="h-8 w-8 text-purple-400" />,
          iconBg: 'from-purple-500/20 to-pink-500/20 border-purple-500/30',
          title: language === 'de' ? 'Premium Feature' : 'Premium Feature',
          description: language === 'de' 
            ? `${featureName || 'Dieses Feature'} ist nur für Premium-Mitglieder verfügbar. Upgrade jetzt für vollen Zugang.`
            : `${featureName || 'This feature'} is only available for Premium members. Upgrade now for full access.`,
          features: [
            {
              icon: <Crown className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Alle Premium-Features freischalten' : 'Unlock all premium features'
            },
            {
              icon: <Brain className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'KI Trading Mentor inklusive' : 'AI Trading Mentor included'
            },
            {
              icon: <Briefcase className="h-5 w-5 text-emerald-400 flex-shrink-0" />,
              text: language === 'de' ? 'Unbegrenzte Trading-Konten' : 'Unlimited trading accounts'
            }
          ],
          buttonText: language === 'de' ? 'Jetzt upgraden' : 'Upgrade now'
        };
      
      default:
        return {
          icon: <Crown className="h-8 w-8 text-purple-400" />,
          iconBg: 'from-purple-500/20 to-pink-500/20 border-purple-500/30',
          title: 'Premium Feature',
          description: '',
          features: [],
          buttonText: 'Upgrade now'
        };
    }
  };

  const content = getContent();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass max-w-md" data-testid="premium-upgrade-modal">
        <DialogHeader>
          <div className={`flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${content.iconBg}`}>
            {content.icon}
          </div>
          <DialogTitle className="text-center text-2xl">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-3">
          {content.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
              {feature.icon}
              <p className="text-sm">{feature.text}</p>
            </div>
          ))}
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            className="flex-1"
            data-testid="premium-modal-later-btn"
          >
            {language === 'de' ? 'Später' : 'Later'}
          </Button>
          <Button 
            onClick={() => navigate('/subscription')} 
            className="flex-1 glow-primary"
            data-testid="premium-modal-upgrade-btn"
          >
            {type === 'pro_screenshot' ? <Camera className="h-4 w-4 mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
            {content.buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumUpgradeModal;
